import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getPendingItems, approveItem, rejectItem } from '../../api/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { Loader2, CheckCircle2, XCircle, ShieldAlert, ExternalLink, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import ImageWithFallback from '../common/ImageWithFallback';

export default function PendingItemsQueue() {
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  const { data: result, isLoading } = useQuery({
    queryKey: ['admin', 'pendingItems'],
    queryFn: getPendingItems,
  });

  const approveMutation = useMutation({
    mutationFn: (id) => approveItem(id),
    onSuccess: () => {
      toast.success(t('itemApprovedSuccess', 'המודעה אושרה ופורסמה בהצלחה!'));
      queryClient.invalidateQueries(['admin', 'pendingItems']);
      queryClient.invalidateQueries(['admin', 'platformStats']);
      queryClient.invalidateQueries(['items']);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to approve item');
    }
  });

  const rejectMutation = useMutation({
    mutationFn: (id) => rejectItem(id),
    onSuccess: () => {
      toast.success(t('itemRejectedSuccess', 'המודעה נדחתה.'));
      queryClient.invalidateQueries(['admin', 'pendingItems']);
      queryClient.invalidateQueries(['admin', 'platformStats']);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to reject item');
    }
  });

  const items = result?.items || [];

  if (isLoading) {
    return (
      <div className="flex justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-black text-foreground flex items-center gap-2">
            <ShieldAlert size={24} className="text-amber-500" />
            <span>{t('pendingApprovalQueue', 'תור אישור מודעות (Moderation Queue)')}</span>
          </h3>
          <p className="text-xs text-muted-foreground font-medium mt-1">
            {t('pendingQueueSub', 'כל המודעות שפורסמו ע״י משתמשים עוברות בדיקה לפני פרסומן לציבור')}
          </p>
        </div>

        <div className="px-4 py-1.5 rounded-full bg-amber-500/15 text-amber-500 border border-amber-500/30 text-xs font-black">
          {items.length} {t('pendingItemsCount', 'מודעות ממתינות')}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="p-12 text-center rounded-3xl bg-card border border-border/50 space-y-3">
          <CheckCircle2 size={48} className="mx-auto text-emerald-500 opacity-60" />
          <h4 className="text-base font-black text-foreground">{t('noPendingItems', 'אין מודעות הממתינות לאישור')}</h4>
          <p className="text-xs text-muted-foreground">{t('allCaughtUp', 'כל המודעות שנשלחו נבדקו ואושרו!')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {items.map((item) => (
            <div 
              key={item._id}
              className="rounded-3xl bg-card border border-border p-5 shadow-lg space-y-4 relative overflow-hidden flex flex-col justify-between"
            >
              <div className="space-y-3">
                {/* Header & User Info */}
                <div className="flex items-center justify-between border-b border-border/50 pb-3">
                  <div className="flex items-center gap-2.5">
                    <ImageWithFallback 
                      src={item.created_by?.avatar} 
                      alt={item.created_by?.full_name} 
                      className="w-8 h-8 rounded-full object-cover border border-border"
                    />
                    <div>
                      <h5 className="text-xs font-black text-foreground">{item.created_by?.full_name || 'User'}</h5>
                      <p className="text-[10px] text-muted-foreground">{item.created_by?.email}</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-500 border border-amber-500/20">
                    {new Date(item.createdAt).toLocaleDateString()}
                  </span>
                </div>

                {/* Main Content & Preview */}
                <div className="flex items-start gap-4">
                  <div className="w-24 h-24 rounded-2xl overflow-hidden bg-muted border border-border shrink-0">
                    {item.images?.[0] ? (
                      <ImageWithFallback src={item.images[0]} alt={item.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                        <ImageIcon size={24} />
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5 flex-1 min-w-0">
                    <h4 className="text-base font-black text-foreground truncate">{item.title}</h4>
                    <p className="text-xs font-bold text-primary">₪{item.estimated_value?.toLocaleString()} • {item.category}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                      {item.description || 'No description provided.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-3 border-t border-border/50 flex items-center gap-3">
                <button
                  onClick={() => approveMutation.mutate(item._id)}
                  disabled={approveMutation.isPending || rejectMutation.isPending}
                  className="flex-1 py-2.5 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all shadow-md active:scale-95 disabled:opacity-50 cursor-pointer"
                >
                  <CheckCircle2 size={16} />
                  <span>{t('approveAndPublish', 'אישור ופרסום')}</span>
                </button>

                <button
                  onClick={() => rejectMutation.mutate(item._id)}
                  disabled={approveMutation.isPending || rejectMutation.isPending}
                  className="py-2.5 px-4 rounded-xl bg-rose-500/15 hover:bg-rose-500/25 text-rose-500 border border-rose-500/30 text-xs font-bold flex items-center justify-center gap-1.5 transition-all active:scale-95 disabled:opacity-50 cursor-pointer"
                >
                  <XCircle size={16} />
                  <span>{t('rejectListing', 'דחייה')}</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
